/**
 * Feather Component and ViewModel Implementation
 * 
 * This file contains the implementation for the FeatherComponent and the associated FeatherDataConnectorViewModel.
 * It provides functionality for configuring and managing Feather Data Connectors, including setting file paths, 
 * date formats, and other data connector properties.
 * 
 * Author: Prajna
 * Date: January 23, 2025
 */

import { ChangeDetectorRef, Injectable } from '@angular/core';
import { Component, Input, OnInit } from '@angular/core';
import { MatDialogConfig, MatDialog } from '@angular/material/dialog';
import { DataConnectorType, DataConnector, FileDataConnector, FeatherDataConnector, FtpServerType, FtpService } from 'src/app/domainObjects/dataConnector';
import { DataSourceType } from 'src/app/domainObjects/dataSourceType';
import { IngestConfigViewModel } from 'src/app/views/import-data/import-data.component';
import { FileBrowserDialogComponent } from 'src/app/views/file-browser-dialog/file-browser-dialog.component';
import { DataConnectorViewModel } from 'src/app/viewModels/dataConnectorViewModel';
import { DataTransformConfigViewModel } from 'src/app/viewModels/dataTransformConfigViewModel';
import { ConfigService } from 'src/app/services/config/config.service';
import { BaseComponent } from 'src/app/base/base.component';
import { ErrorReportingService } from 'src/app/services/errorReporting/error-reporting.service';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { FormBuilder, UntypedFormControl } from '@angular/forms';
import { map, startWith } from 'rxjs/operators';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SharedService } from 'src/app/services/shared.service';
import { UserDataService } from 'src/app/services/user-data-service';
// import { IngestParametersSetting, IngestParametersSettings } from 'src/app/domainObjects/masterConfig';
import { FolderBrowserDialogComponent } from 'src/app/views/folder-browser-dialog/folder-browser-dialog.component';
import { DatePipe } from '@angular/common';
import { UpdateingestnodestatusService } from 'src/app/services/updateingestnodestatus.service';
import { FtpConnectionStatus, ftpNotReadyMessage } from 'src/app/views/shared/ftp-service-select/ftp-service-select.component';

export class FeatherDataConnectorViewModel extends DataConnectorViewModel<FeatherDataConnector> {
  id: number;
  fileNameFormat = '';
  scheduleDelimiter='';
  connectionTimeOut: number;
  scheduleConnectionTimeOut='';
  inputFileNameFileScheduleDirectory='';
  baseFileName='';
  dateTimeExtension: string = '';  // Date-Time extension
  selectedInstance= '';
  nValue= '';
  currentDate: string;  // Holds the current date in YYYY-MM-DD format
  fileExtension='';

  constructor(featherDataConnector?: FeatherDataConnector) {
    super();
    this.dataConnectorType = DataConnectorType.Feather;
    const today = new Date();
    this.currentDate = today.toISOString().split('T')[0]; // Extracts the date part in YYYY-MM-DD format
   
     // Set the defualt values
     this.scheduleConnectionTimeOut="180"
     this.scheduleDelimiter='';
     this.baseFileName='';
     this.dateTimeExtension = 'yyyy-MM-dd';  // Set the current date as default
     this.nValue='';
     this.fileExtension='.ftr';
     this.isUseFtp =false;
     this.ftpService = new FtpService(FtpServerType.SFTP,"","","", 22);
    if (featherDataConnector) {
      this.inputFileName = featherDataConnector.filePath;
      this.fileNameFormat = featherDataConnector.fileNameFormat;
      this.id = featherDataConnector.id;
      this.connectionTimeOut = featherDataConnector.connectionTimeOut;
      this.selectedInstance = featherDataConnector.selectedInstance;
      this.nValue = featherDataConnector.nValue;
      this.isUseFtp = featherDataConnector.isUseFtp;
      this.ftpService = featherDataConnector.ftpService;
    }
  }

  toDataConnector(): FeatherDataConnector {
    return new FeatherDataConnector(this.id, this.inputFileName, this.fileNameFormat, this.connectionTimeOut,this.selectedInstance,this.nValue,this.isUseFtp,this.ftpService);
  }
}
/**
 * FeatherComponent
 * Provides the UI and logic for configuring Feather Data Connectors.
 */
@Component({
    selector: 'app-feather',
    templateUrl: './feather.component.html',
    styleUrls: ['./feather.component.css'],
    standalone: false
})

// @Injectable({
//   providedIn: 'root'
// })

export class FeatherComponent extends BaseComponent implements OnInit {
  @Input() dataSourceType: DataSourceType;
  @Input() dataTransformConfigViewModel: DataTransformConfigViewModel;
  @Input() ingestConfigViewModel: IngestConfigViewModel;
  //@Input() viewModel: FileDataConnectorViewModel;
  isLoading = new BehaviorSubject<boolean>(false);
  
  // myControl = new UntypedFormControl('');
  delimiterOptions: Observable<string[]>;
  formattedDate: string;
  selectedFormat: string = 'yyyy-MM-dd'; // Default format
  viewModel: FeatherDataConnectorViewModel;
  isInstanceDisabled:boolean;
  private subscription!: Subscription;
  
  constructor(
    private changeDet: ChangeDetectorRef, 
    private configService: ConfigService,
    private sharedService: SharedService,
    private userData: UserDataService,
    private updateingestnodestatusService: UpdateingestnodestatusService,
    matDialog: MatDialog,
    private snackBar: MatSnackBar,
    public datePipe: DatePipe,
    errorLogger: ErrorReportingService,private fb: FormBuilder) {
      super(errorLogger, matDialog);
      this.isInstanceDisabled= false;
    }

   /**
   * Updates the formatted date based on the selected format.
   */
    updateDate() {
      const currentDate = new Date();
      this.formattedDate = this.datePipe.transform(currentDate, this.selectedFormat) || '';
    }
    /**
   * Handles changes in date format.
   * @param newFormat - The new date format selected.
   */
  onFormatChange(newFormat: string) {
  this.selectedFormat = newFormat;
  this.updateDate();
}
  ngOnInit(): void {
 
    this.updateDate();
    this.subscription = this.sharedService.getEvent().subscribe(data => {
      // console.log('Received event in Child One:', data);
      this.changeDet.detectChanges();
      // Handle the event data here
    });
   if(!this.viewModel.ftpService){
    this.viewModel.ftpService=new FtpService(FtpServerType.SFTP,"","","", 22);
   }
  }
  ngOnDestroy() {
    this.subscription.unsubscribe(); // Clean up to avoid memory leaks
  }
  ngOnChanges(changes: any) {
    this.viewModel = changes.ingestConfigViewModel?.currentValue.getFeatherDataConnector();
    if (!this.viewModel || this.viewModel.dataConnectorType!=DataConnectorType.Feather) {
      this.viewModel = new FeatherDataConnectorViewModel();
      this.viewModel.ftpService=new FtpService(FtpServerType.SFTP,"","","", 22);
      this.viewModel.dataSourceType = this.dataSourceType;
      this.ingestConfigViewModel.dataConnector = this.viewModel;
    } else {

    }
  }

  /** Connection test state of the selected FTP service; FTP browsing waits for 'success'. */
  ftpConnectionStatus: FtpConnectionStatus = 'none';

  onFtpChange(updated: FtpService) {
    this.viewModel.ftpService = updated;
  }

  async handleBlur(value: string) {
    await this.getFeatherFileExistCheck(value);
  }
  async getFeatherFileExistCheck(filePath: string) {
    const busyDialog = this.showBusyDialog('Validating file name existence...');
    try {
      // Set the input file name
      this.viewModel.inputFileName = filePath;
  
      // Call the API and wait for the response
      const response = await this.configService.getFeatherFileExistCheck(this.viewModel.inputFileName);
      if (response?.fileExistsStatus === false) {
        this.displayError("The file or file path of the Developer File Path was not found or incorrectly entered.", "File or File path not found");    
        this.viewModel.inputFileName = "";
        this.changeDet.detectChanges();
        return; // Exit early if file doesn't exist
      }
      // If file exists, proceed with further logic (if any)
      // You can add your sheet name logic here
    } catch (err) {
      const errorMessage = err?.error || err?.message || '';
      const statusCode = err?.status;
      if (statusCode === 500 || errorMessage.includes('500 Internal Server Error')) {
        this.displayError("The file or file path of the Developer File Path was not found or incorrectly entered.", "File or File path not found");  
        this.viewModel.inputFileNameExcel = "";
        this.changeDet.detectChanges();
      } else {
        this.displayError('Failed to get Sheet Name', 'Error', err, (result) => {});
      }
  
      this.changeDet.detectChanges();
    } finally {
      busyDialog.close();
    }
  }
  /**
   * Opens the file browser dialog for selecting files.
   */
  showFileBrowserDialog(): void {
    const ftpNotReady = ftpNotReadyMessage(this.viewModel.isUseFtp, this.ftpConnectionStatus);
    if (ftpNotReady) {
      this.displayError(ftpNotReady, 'FTP Service');
      return;
    }
    console.log("showFileBrowserDialog this.ingestConfigViewModel.dataConnector====",this.viewModel.inputFileName);
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '750px';
    dialogConfig.data = {dataSourceTypeId: this.dataSourceType, supportedFileTypes: ['.ftr', '.feather'],isUseFtp:this.viewModel.isUseFtp,ftpService:this.viewModel.ftpService, ingestFilePath:this.viewModel.inputFileName, ingestDataConnectorType: this.viewModel.dataConnectorType};

    const dialogRef = this.matDialog.open(FileBrowserDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (!result.selectedFileName) { return; }

      // File chosen is
      //console.log('User selected to import file: ' + result.selectedFileName);
      const previousFileName = this.viewModel.inputFileName;
      this.viewModel.inputFileName = result.selectedFileName;

      // TODO: Determine why I need to invoke the below code to get UI reflecting the inputFileName
      this.changeDet.detectChanges();

      if (this.ingestConfigViewModel.hasMappedSourceChanged(previousFileName, result.selectedFileName)) {
        // The old Column Map refers to the previous file's columns — clear it and let the
        // Analyst know right away that it needs to be reconfigured.
        this.ingestConfigViewModel.columnMap = [];
      }
      if (this.ingestConfigViewModel.columnMap.length === 0) {
        this.displayWarning(
          'Please configure the Column Mapper before saving.',
          'Column Mapper Not Configured',
          null,
          () => {
            this.sharedService.sendEvent({ type: 'open_mapper_panel', guid: this.ingestConfigViewModel.guid });
          }
        );
        return;
      }
    });
  }
    /**
   * Opens the folder browser dialog for selecting directories.
   */
  browseDir(){
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.width = '750px';
    dialogConfig.data = {dataSourceTypeId: 1, supportedFileTypes: ['.csv', '.txt']};

    const dialogRef = this.matDialog.open(FolderBrowserDialogComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(result => {
      if (!result.selectedFileName) { return; }

      // File chosen is
      //console.log('User selected to import file: ' + result.selectedFileName);
      this.viewModel.inputFileNameFileScheduleDirectory= result.selectedFileName;
       // Focus back to the input field if necessary
     // Focus back to the input field if necessary

    //  this.formGroup[fieldname] = new FormControl(result.selectedFileName);
      //this.form = this.formBuilder.group(this.formGroup);
       // Ensure the input field regains focus
        this.changeDet.detectChanges();
    });
  }
 
  handleBlurDirectory(inputFileNameExcel: string) {
    setTimeout(() => {
      // Your logic to handle the blur event
      console.log('Directory:', inputFileNameExcel);
    }, 200);
  }
  /**
   * Handles selection changes for the instance dropdown.
   * @param selectedValue - The selected value from the dropdown.
   */
    onSelectionChange(selectedValue: string) {
      if (selectedValue === 'first') {
        this.viewModel.nValue = '1';
        this.isInstanceDisabled = true;  // Freeze the instance field
      } else if (selectedValue === 'nth') {
        this.viewModel.nValue = '1'; // Keep it blank for nth
        this.isInstanceDisabled = false;  // Freeze the instance field
      } else {
        this.viewModel.nValue = ''; // Keep it blank for nth
        this.isInstanceDisabled = true;  // Freeze the instance field
      }
    }
}
